import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { OccupancyBlockCostClass } from '../../shared/models/occupancy-dashboard.model';
import { ConversionMode, GroupKey } from './engine';
import { C, COST_TONE, Tone, fmtPct } from './theme';

export interface Option<T extends string = string> {
  value: T;
  label: string;
}

export interface SortState {
  key: string;
  dir: 'asc' | 'desc';
}

export type ValueBasis = 'expected' | 'exposure';
export type CostDim = 'reason' | 'owner' | 'project';

export const GROUP_OPTIONS: Option<GroupKey>[] = [
  { value: 'project', label: 'Project' },
  { value: 'area', label: 'Area' },
  { value: 'view', label: 'View' },
  { value: 'unitType', label: 'Unit type' },
  { value: 'unitTypeGroup', label: 'Unit type group' },
  { value: 'landlord', label: 'Landlord' },
  { value: 'building', label: 'Building' }
];

export const CONVERSION_OPTIONS: Option<ConversionMode>[] = [
  { value: 'fixed', label: 'Fixed rate' },
  { value: 'actual', label: 'Actual by type & month' }
];

export const VALUE_OPTIONS: Option<ValueBasis>[] = [
  { value: 'expected', label: 'Expected loss' },
  { value: 'exposure', label: 'Exposure' }
];

/** Blocks carry no department in production, so the cost breakdowns are by type, person, project. */
export const COST_DIMS: Option<CostDim>[] = [
  { value: 'reason', label: 'Block type' },
  { value: 'owner', label: 'Raised by' },
  { value: 'project', label: 'Project' }
];

export function Pill({ value, tone }: { value: number | null; tone: (v: number | null) => Tone }) {
  const t = tone(value);
  return (
    <span
      className="inline-flex min-w-[62px] justify-center rounded-full px-2.5 py-1 text-xs font-semibold tabular-nums"
      style={{ background: t.bg, color: t.fg }}
    >
      {fmtPct(value, 0)}
    </span>
  );
}

export function Tag({ kind }: { kind: OccupancyBlockCostClass }) {
  const t = COST_TONE[kind];
  if (!t) return null;
  return (
    <span
      className="rounded px-1.5 py-0.5 text-[10px] font-semibold"
      style={{ background: t.bg, color: t.fg }}
    >
      {t.label}
    </span>
  );
}

const optLabel = (options: Option[], v: string): string => {
  const found = options.find((o) => o.value === v);
  return found ? found.label : v;
};

export function MultiSelect({
  label,
  options,
  value,
  onChange
}: {
  label: string;
  options: Option[];
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) =>
      ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const shown = options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase()));
  const summary =
    value.length === 0
      ? label
      : value.length === 1
      ? optLabel(options, value[0])
      : `${label}: ${value.length}`;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 rounded-md border bg-white px-3 py-2 text-left text-sm"
        style={{
          borderColor: value.length ? C.green : C.line,
          color: value.length ? C.ink : C.muted
        }}
      >
        <span className="truncate">{summary}</span>
        <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" className="shrink-0">
          <path
            d="M2 4l4 4 4-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
      </button>
      {open && (
        <div
          className="absolute z-30 mt-1 w-full min-w-[210px] rounded-md border bg-white p-2 shadow-lg"
          style={{ borderColor: C.line }}
        >
          {options.length > 7 && (
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search"
              className="mb-2 w-full rounded border px-2 py-1 text-xs outline-none"
              style={{ borderColor: C.line }}
            />
          )}
          <div className="max-h-56 overflow-auto">
            {shown.map((o) => {
              const on = value.includes(o.value);
              return (
                <label
                  key={o.value}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-slate-50"
                >
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() =>
                      onChange(on ? value.filter((v) => v !== o.value) : [...value, o.value])
                    }
                    className="h-3.5 w-3.5 shrink-0 accent-emerald-800"
                  />
                  <span className="truncate" style={{ color: C.ink }}>
                    {o.label}
                  </span>
                </label>
              );
            })}
            {shown.length === 0 && (
              <p className="px-2 py-3 text-xs" style={{ color: C.muted }}>
                Nothing matches that search.
              </p>
            )}
          </div>
          {value.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="mt-2 w-full rounded px-2 py-1 text-xs font-medium hover:bg-slate-50"
              style={{ color: C.green }}
            >
              {'Clear '}
              {label.toLowerCase()}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function Metric({
  label,
  value,
  sub,
  accent
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  accent?: string;
}) {
  return (
    <div className="rounded-lg border bg-white px-4 py-3" style={{ borderColor: C.line }}>
      <p className="text-[11px] font-medium" style={{ color: C.muted }}>
        {label}
      </p>
      <p className="mt-1.5 text-2xl font-semibold tabular-nums" style={{ color: accent || C.ink }}>
        {value}
      </p>
      {sub && (
        <p className="mt-0.5 text-[11px]" style={{ color: C.muted }}>
          {sub}
        </p>
      )}
    </div>
  );
}

export function SortHeader({
  children,
  column,
  sort,
  setSort,
  align = 'left'
}: {
  children: ReactNode;
  column: string;
  sort: SortState;
  setSort: (s: SortState) => void;
  align?: 'left' | 'right';
}) {
  const active = sort.key === column;
  return (
    <th
      scope="col"
      onClick={() => setSort({ key: column, dir: active && sort.dir === 'desc' ? 'asc' : 'desc' })}
      className={`cursor-pointer select-none whitespace-nowrap px-4 py-3 text-[11px] font-semibold ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
      style={{ color: active ? C.green : C.muted }}
    >
      {children}
      <span className="ml-1 inline-block w-2">
        {active ? (sort.dir === 'desc' ? '↓' : '↑') : ''}
      </span>
    </th>
  );
}

export function Card({
  title,
  note,
  right,
  children
}: {
  title: ReactNode;
  note?: ReactNode;
  right?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="rounded-lg border bg-white" style={{ borderColor: C.line }}>
      <div
        className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3"
        style={{ borderColor: C.line }}
      >
        <div>
          <h2 className="text-sm font-semibold">{title}</h2>
          {note && (
            <p className="mt-0.5 text-xs" style={{ color: C.muted }}>
              {note}
            </p>
          )}
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

export function Segmented<T extends string>({
  value,
  onChange,
  options,
  label
}: {
  value: T;
  onChange: (v: T) => void;
  options: Option<T>[];
  label?: string;
}) {
  return (
    <div className="flex items-center gap-2">
      {label && (
        <span className="text-xs" style={{ color: C.muted }}>
          {label}
        </span>
      )}
      <div
        className="inline-flex rounded-md border p-0.5"
        style={{ borderColor: C.line, background: '#fff' }}
      >
        {options.map((o) => (
          <button
            type="button"
            key={o.value}
            onClick={() => onChange(o.value)}
            className="rounded px-2.5 py-1 text-xs font-medium"
            style={value === o.value ? { background: C.green, color: '#fff' } : { color: C.muted }}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export interface TipRow {
  k: string;
  v: ReactNode;
  color?: string;
}

/** Recharts tooltip body. `rows` turns the hovered datum into label/value lines. */
export function ChartTip({
  active,
  payload,
  label,
  rows
}: {
  active?: boolean;
  payload?: any[];
  label?: string;
  rows: (p: any) => TipRow[];
}) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div
      className="rounded-md border bg-white px-3 py-2 text-xs shadow-md"
      style={{ borderColor: C.line }}
    >
      <p className="mb-1 font-semibold">{p.long || p.tip || label}</p>
      {rows(p).map((r) => (
        <p key={r.k} className="flex justify-between gap-6" style={{ color: r.color || C.ink }}>
          <span>{r.k}</span>
          <span className="font-medium tabular-nums">{r.v}</span>
        </p>
      ))}
    </div>
  );
}

export function SearchBox({
  search,
  setSearch
}: {
  search: string;
  setSearch: (v: string) => void;
}) {
  return (
    <input
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      placeholder="Find a unit or unit ID"
      className="w-56 rounded-md border bg-white px-3 py-1.5 text-xs outline-none"
      style={{ borderColor: C.line }}
    />
  );
}

export function EmptyRow({ cols }: { cols: number }) {
  return (
    <tr>
      <td colSpan={cols} className="px-4 py-10 text-center text-sm" style={{ color: C.muted }}>
        Nothing matches this filter set. Widen the date range or clear a filter.
      </td>
    </tr>
  );
}

export function GroupPicker({
  groupBy,
  setGroupBy
}: {
  groupBy: GroupKey;
  setGroupBy: (g: GroupKey) => void;
}) {
  return (
    <select
      value={groupBy}
      onChange={(e) => setGroupBy(e.target.value as GroupKey)}
      className="rounded-md border bg-white px-2 py-1.5 text-xs"
      style={{ borderColor: C.line }}
      aria-label="Group by"
    >
      {GROUP_OPTIONS.map((o) => (
        <option key={o.value} value={o.value}>
          {'Group by '}
          {o.label.toLowerCase()}
        </option>
      ))}
    </select>
  );
}

export function ConversionControl({
  mode,
  setMode,
  rate,
  setRate
}: {
  mode: ConversionMode;
  setMode: (m: ConversionMode) => void;
  rate: number;
  setRate: (r: number) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Segmented label="Conversion" value={mode} onChange={setMode} options={CONVERSION_OPTIONS} />
      {mode === 'fixed' && (
        <label
          className="flex items-center gap-1 rounded-md border bg-white px-2 py-1 text-xs"
          style={{ borderColor: C.line }}
        >
          <input
            type="number"
            min={0}
            max={100}
            step={5}
            value={rate}
            onChange={(e) => setRate(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
            className="w-12 bg-transparent text-right tabular-nums outline-none"
            aria-label="Fixed conversion rate"
          />
          <span style={{ color: C.muted }}>%</span>
        </label>
      )}
    </div>
  );
}
